import {
  Content,
  Header,
  Page,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import type { UseCockpitControllerOptions } from '../controller/useCockpitController';
import { useCockpitController } from '../controller/useCockpitController';
import { CockpitHeader } from './CockpitHeader';
import { CockpitShell } from './CockpitShell';

export type CockpitActivityPageProps = UseCockpitControllerOptions & {
  loadingTitle: string;
  showArtifactLane?: boolean;
  /** Neutral port forwarded to CockpitHeader; see CockpitHeaderProps. */
  headerActions?: React.ReactNode;
  /** Opaque host-owned scope used only to isolate the local metrics preference. */
  metricsPreferenceScope?: string | null;
};

const PageState = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Page themeId="tool">
    <Header title={title} />
    <Content>{children}</Content>
  </Page>
);

const LoadingState = ({ title }: { title: string }) => (
  <PageState title={title}>
    <Progress />
  </PageState>
);

export const CockpitActivityPage = ({
  loadingTitle,
  showArtifactLane = true,
  headerActions,
  metricsPreferenceScope,
  ...controllerOptions
}: CockpitActivityPageProps) => {
  const state = useCockpitController(controllerOptions);
  if (state.loading && !state.model) {
    return <LoadingState title={loadingTitle} />;
  }
  if (!state.model) {
    return (
      <PageState title="Cockpit indisponível">
        <ResponseErrorPanel
          error={state.error ?? new Error('Atividade não encontrada.')}
        />
      </PageState>
    );
  }
  return (
    <Page themeId="tool">
      <CockpitHeader
        header={state.model.header}
        refreshing={state.refreshing}
        stale={state.stale}
        refreshFailed={state.error !== null}
        onRefresh={state.refresh}
        onOpenRevision={state.openRevision}
        headerActions={headerActions}
      />
      <Content>
        <CockpitShell
          model={state.model}
          showArtifactLane={showArtifactLane}
          stale={state.stale}
          metricsPreferenceScope={metricsPreferenceScope}
          selection={state.selection}
          artifactContent={state.artifactContent}
          loadArtifactContent={state.loadArtifactContent}
          loadMoreArtifactContent={state.loadMoreArtifactContent}
          timeline={state.timeline}
          setTimelineQuery={state.setTimelineQuery}
          loadMoreTimeline={state.loadMoreTimeline}
        />
      </Content>
    </Page>
  );
};
